import { Link } from "react-router-dom";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";
import { useEffect, useState } from "react";
import moment from "moment";
import { MapPin, Wind } from "lucide-react";
import {
  dashboardSummary,
  getDashboardReport,
  getDashboard,
  getTopItems,
  getCollectionReport,
} from "../../services/services";
import Loading from "../../components/loading";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import CustomDateRangePicker from "./CustomDateRangePicker";
import { useSelector } from "react-redux";

const Dashboard = () => {
  const [items, setItems] = useState(0);
  const [salesCount, setSalesCount] = useState(0);
  const [purchasesCount, setPurchasesCount] = useState(0);
  const [parties, setParties] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [salesAmount, setSalesAmount] = useState(0);
  const [purchaseAmount, setPurchaseAmount] = useState(0);
  const [topProducts, setTopProducts] = useState([]);
  const [periodSales, setPeriodSales] = useState("last7days");
  const [periodTop, setPeriodTop] = useState("last7days");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showLocationPrompt, setShowLocationPrompt] = useState(true);
  const [weather, setWeather] = useState(null);
  const [currentTime, setCurrentTime] = useState(moment().format("hh:mm A"));
  const [loadingWeather, setLoadingWeather] = useState(false);

  const [collection, setCollection] = useState(null);

  const { user } = useSelector((state) => state.auth);
  const { activeBusiness } = useSelector((state) => state.business);

  // Clock Update
  // useEffect(() => {
  //   const timer = setInterval(
  //     () => setCurrentTime(moment().format("hh:mm A")),
  //     60000,
  //   );
  //   return () => clearInterval(timer);
  // }, []);

  useEffect(() => {
    if (!activeBusiness?.city) return;

    const savedWeather = localStorage.getItem("weatherData");
    const savedCity = localStorage.getItem("weatherCity");
    const savedTime = localStorage.getItem("weatherTime");

    const now = Date.now();

    // 1 hour cache
    const oneHour = 10 * 1000;

    if (
      savedWeather &&
      savedCity === activeBusiness.city &&
      savedTime &&
      now - Number(savedTime) < oneHour
    ) {
      // console.log(" Weather Loaded From Cache");
      // console.log(
      //   "Cache Time:",
      //   moment(Number(savedTime)).format("hh:mm:ss A")
      // );
      setWeather(JSON.parse(savedWeather));
    } else {
      console.log("Cache Expired / No Cache → Calling API");
      fetchWeatherByCity();
    }
  }, [activeBusiness?.city]);

  const fetchWeatherByCity = async () => {
    if (!activeBusiness?.city) return;

    try {
      setLoadingWeather(true);
      // console.log("Fetching Fresh Weather API...");

      const API_KEY = process.env.REACT_APP_WEATHER_API_KEY;

      const res = await fetch(
        `https://api.openweathermap.org/data/2.5/weather?q=${activeBusiness.city}&appid=${API_KEY}&units=metric`,
      );

      const data = await res.json();

      if (data?.main) {
        console.log(" Fresh Weather Received");
        console.log("Fetched At:", moment(currentTime).format("hh:mm:ss A"));
        console.log("Temp:", data.main.temp);
        console.log("Wind:", data.wind?.speed);

        setWeather(data);
        localStorage.setItem("weatherData", JSON.stringify(data));
        localStorage.setItem("weatherCity", activeBusiness.city);
        localStorage.setItem("weatherTime", Date.now());
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingWeather(false);
    }
  };

  const fetchCollectionReport = async () => {
    try {
      const res = await getCollectionReport(buildDatePayload(periodSales));
      if (res.data.status) {
        setCollection(res?.data?.response || null);
      }
    } catch (err) {
      console.error("Failed to fetch collection report", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCollectionReport();
  }, [periodSales, fromDate, toDate]);

  const buildDatePayload = (period) => ({
    date: period,
    fromDate: period === "custom-range" ? fromDate : "",
    toDate: period === "custom-range" ? toDate : "",
  });

  useEffect(() => {
    if (!user?._id || !activeBusiness?._id) return;
    const fetchInitial = async () => {
      try {
        setLoading(true);
        const res = await dashboardSummary({
          businessId: activeBusiness._id,
        });
        const data = res?.data?.response;

        setItems(data?.totalItems || 0);
        setParties(data?.totalParties || 0);
        setSalesCount(data?.totalSales || 0);
        setPurchasesCount(data?.totalPurchases || 0);
        setTransactions(data?.recentTransactions || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchInitial();
  }, [user?._id, activeBusiness?._id]);

  useEffect(() => {
    if (!user?._id || !activeBusiness?._id) return;

    const fetchDashboardReport = async () => {
      const res = await getDashboardReport({
        ...buildDatePayload(periodSales),
        businessId: activeBusiness._id,
      });
      const report = res?.data?.response || {};
      setSalesAmount(report.sales?.totalAmount || 0);
      setPurchaseAmount(report.purchases?.totalAmount || 0);
    };
    fetchDashboardReport();
  }, [user?._id, activeBusiness?._id, periodSales, fromDate, toDate]);

  useEffect(() => {
    if (!user?._id || !activeBusiness?._id) return;
    const fetchTopItems = async () => {
      try {
        const res = await getTopItems({
          ...buildDatePayload(periodTop),
          businessId: activeBusiness._id,
        });
        setTopProducts(
          (res?.data.response || []).map((i) => ({
            name: i.itemName || "Unknown",
            value: i.totalQty || 0,
          })),
        );
      } catch (err) {
        console.error("Failed to fetch top items", err);
      }
    };
    fetchTopItems();
  }, [user?._id, activeBusiness?._id, periodTop, fromDate, toDate]);

  const hasCity = !!activeBusiness?.city;
  // const hasWeather = weather && weather?.main;
  // const hasWeather = weather?.main && weather?.wind;
  const hasWeather = !!weather?.main;
  // if (loading) return <Loading />;

  return (
    <div id="main">
      <h4 className="custom-title mb-4" style={{ color: "#0b2b6d" }}>
        <span className="fw-normal">Welcome,</span>{" "}
        <span className="fw-semibold">{activeBusiness?.businessName}</span>
      </h4>

      <div className="row g-3 mb-4">
        <div className="col-lg-8">
          <div className="row g-3 mb-4">
            {[
              {
                title: "Items",
                val: items,
                icon: "bi-box",
                bg: "bg-primary",
                link: "/items",
              },
              {
                title: "Sales",
                val: salesCount,
                icon: "bi-cart-check",
                bg: "bg-success",
                link: "/sales",
              },
              {
                title: "Purchases",
                val: purchasesCount,
                icon: "bi-cart-plus",
                bg: "bg-warning",
                link: "/purchases",
              },
              {
                title: "Parties",
                val: parties,
                icon: "bi-people",
                bg: "bg-info",
                link: "/party",
              },
            ].map((card, i) => (
              <div className="col-6 col-lg-3" key={i}>
                <Link to={card.link} className="text-decoration-none">
                  <div className="card border-0 shadow-sm h-100 py-0">
                    <div className="card-body p-3 pb-0">
                      <div className="d-flex align-items-center gap-3">
                        <div
                          className={`rounded-circle ${card.bg} text-white d-flex align-items-center justify-content-center flex-shrink-0`}
                          style={{
                            width: "clamp(30px, 10vw, 40px)",
                            height: "clamp(30px, 10vw, 40px)",
                          }}
                        >
                          <i className={`bi ${card.icon} fs-6`}></i>
                        </div>

                        <div className="flex-grow-1 overflow-hidden">
                          <p
                            className="text-muted small mb-0 fw-bold text-truncate"
                            title={card.title}
                          >
                            {card.title}
                          </p>
                        </div>
                      </div>

                      <div className="mt-3">
                        <h4 className="mb-0 fw-bold text-dark">{card.val}</h4>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>

          {/* - Reports */}
          <div>
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h6 className="mb-0 fw-bold">Sales vs Purchases</h6>
                <div className="d-flex gap-2">
                  {periodSales === "custom-range" && (
                    <div className="d-flex gap-1">
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                      />
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                      />
                    </div>
                  )}
                  <select
                    className="form-select form-select-sm w-auto"
                    value={periodSales}
                    onChange={(e) => setPeriodSales(e.target.value)}
                  >
                    <option value="last7days">Last 7 Days</option>
                    <option value="today">Today</option>
                    <option value="last30days">Last 30 Days</option>
                    <option value="thisMonth">This Month</option>
                    <option value="lastMonth">Last Month</option>
                    <option value="custom-range">Custom Range</option>
                  </select>
                </div>
              </div>

              <div className="card-body" style={{ minHeight: "300px" }}>
                {salesAmount + purchaseAmount === 0 ? (
                  <div className="text-center py-5 text-muted small">
                    <i className="bi bi-circle-half display-1 text-secondary mb-3"></i>
                    <p>No data for selected period</p>
                  </div>
                ) : (
                  <>
                    <div className="row g-4 text-center">
                      {/* Sales Circle */}
                      <div className="col-6">
                        <h6 className="fw-bold text-success mb-2">Sales</h6>

                        <div
                          style={{
                            position: "relative",
                            width: "150px",
                            height: "150px",
                            margin: "0 auto",
                          }}
                        >
                          <svg width="140" height="140" viewBox="0 0 140 140">
                            {/* Background circle (gray) */}
                            <circle
                              cx="70"
                              cy="70"
                              r="60"
                              fill="none"
                              stroke="#e9ecef"
                              strokeWidth="20"
                            />
                            {/* Progress circle (green) */}
                            <circle
                              cx="70"
                              cy="70"
                              r="60"
                              fill="none"
                              stroke="#198754"
                              strokeWidth="20"
                              strokeDasharray="377 377" // circumference ≈ 2 * π * 60
                              strokeDashoffset={
                                377 *
                                (1 -
                                  salesAmount /
                                    Math.max(salesAmount + purchaseAmount, 1))
                              }
                              transform="rotate(-90 70 70)" // start from top
                            />
                          </svg>

                          {/* Center value */}
                          <div
                            style={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              textAlign: "center",
                            }}
                          ></div>
                          <div className="fw-bold fs-5 mt-4 text-success">
                            ₹{" "}
                            {(Number(salesAmount) || 0).toLocaleString("en-IN")}
                          </div>
                        </div>
                      </div>

                      {/* Purchases Circle */}
                      <div className="col-6">
                        <h6 className="fw-bold text-warning mb-2">Purchases</h6>

                        <div
                          style={{
                            position: "relative",
                            width: "150px",
                            height: "150px",
                            margin: "0 auto",
                          }}
                        >
                          <svg width="140" height="140" viewBox="0 0 140 140">
                            {/* Background circle */}
                            <circle
                              cx="70"
                              cy="70"
                              r="60"
                              fill="none"
                              stroke="#e9ecef"
                              strokeWidth="20"
                            />
                            {/* Progress circle (warning/orange) */}
                            <circle
                              cx="70"
                              cy="70"
                              r="60"
                              fill="none"
                              stroke="#ffc107"
                              strokeWidth="20"
                              strokeDasharray="377 377"
                              strokeDashoffset={
                                377 *
                                (1 -
                                  purchaseAmount /
                                    Math.max(salesAmount + purchaseAmount, 1))
                              }
                              transform="rotate(-90 70 70)"
                            />
                          </svg>

                          {/* Center value */}
                          <div
                            style={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              textAlign: "center",
                            }}
                          ></div>
                        </div>
                        <div className="fw-bold fs-5 mt-3 text-warning">
                          ₹{" "}
                          {(Number(purchaseAmount) || 0).toLocaleString(
                            "en-IN",
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Collection Summary */}
                    <div className="row mt-4 pt-4 border-top">
                      <div className="card-header pt-0 border-bottom-0 col-12">
                        <h6 className="fw-bold mb-3">Collection Summary</h6>
                        <div className="row g-4">
                          <div className="col-6 col-md-4">
                            <div className="p-2 bg-light rounded">
                              <strong className="text-success">
                                ₹{" "}
                                {(Number(collection?.cash) || 0).toLocaleString(
                                  "en-IN",
                                )}
                              </strong>
                              <small
                                className="text-muted d-block mt-1"
                                style={{ fontSize: "12px" }}
                              >
                                via Cash
                              </small>
                            </div>
                          </div>
                          <div className="col-6 col-md-4">
                            <div className="p-2 bg-light rounded">
                              <strong className="text-primary">
                                ₹{" "}
                                {(Number(collection?.bank) || 0).toLocaleString(
                                  "en-IN",
                                )}
                              </strong>
                              <small
                                className="text-muted d-block mt-1"
                                style={{ fontSize: "12px" }}
                              >
                                via Bank
                              </small>
                            </div>
                          </div>
                          <div className="col-6 col-md-4">
                            <div className="p-2 bg-light rounded">
                              <strong className="text-info">
                                ₹{" "}
                                {(Number(collection?.upi) || 0).toLocaleString(
                                  "en-IN",
                                )}
                              </strong>
                              <small
                                className="text-muted d-block mt-1"
                                style={{ fontSize: "12px" }}
                              >
                                via UPI
                              </small>
                            </div>
                          </div>
                          <div className="col-6 col-md-4">
                            <div className="p-2 bg-light rounded">
                              <strong className="text-warning">
                                ₹{" "}
                                {(Number(collection?.card) || 0).toLocaleString(
                                  "en-IN",
                                )}
                              </strong>
                              <small
                                className="text-muted d-block mt-1"
                                style={{ fontSize: "12px" }}
                              >
                                via Card
                              </small>
                            </div>
                          </div>
                          <div className="col-6 col-md-4">
                            <div className="p-2 bg-light rounded">
                              <strong className="text-danger">
                                ₹{" "}
                                {(
                                  Number(collection?.online) || 0
                                ).toLocaleString("en-IN")}
                              </strong>
                              <small
                                className="text-muted d-block mt-1"
                                style={{ fontSize: "12px" }}
                              >
                                via Online
                              </small>
                            </div>
                          </div>
                          <div className="col-6 col-md-4">
                            <div className="p-2 bg-success bg-opacity-10 rounded">
                              <strong className="text-success">
                                ₹{" "}
                                {(
                                  (Number(collection?.cash) || 0) +
                                  (Number(collection?.bank) || 0) +
                                  (Number(collection?.upi) || 0) +
                                  (Number(collection?.card) || 0) +
                                  (Number(collection?.online) || 0)
                                ).toLocaleString("en-IN")}
                              </strong>
                              <small
                                className="text-muted d-block mt-1"
                                style={{ fontSize: "12px" }}
                              >
                                Total
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h6 className="mb-0 fw-bold">Top Selling Items</h6>
                <div className="d-flex gap-2">
                  {periodTop === "custom-range" && (
                    <div className="d-flex gap-1">
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                      />
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                      />
                    </div>
                  )}
                  <select
                    className="form-select form-select-sm w-auto"
                    value={periodTop}
                    onChange={(e) => setPeriodTop(e.target.value)}
                  >
                    <option value="last7days">Last 7 Days</option>
                    <option value="today">Today</option>
                    <option value="last30days">Last 30 Days</option>
                    <option value="thisMonth">This Month</option>
                    <option value="lastMonth">Last Month</option>
                    <option value="custom-range">Custom Range</option>
                  </select>
                </div>
              </div>
              <div
                className="card-body d-flex align-items-center justify-content-center"
                style={{ minHeight: "280px" }}
              >
                {topProducts?.length === 0 || !topProducts ? (
                  <div className="text-center text-muted">
                    <i className="bi bi-graph-up display-1 text-secondary mb-3"></i>
                    <p className="mb-0 small">
                      No top selling items in selected period
                    </p>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={topProducts}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#f1f5f9"
                      />
                      <XAxis
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        fontSize={12}
                        angle={-25}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis axisLine={false} tickLine={false} fontSize={12} />
                      <Tooltip
                        cursor={{ fill: "#f8fafc" }}
                        formatter={(value) => [`${value} units`, "Quantity"]}
                      />
                      <Bar
                        dataKey="value"
                        fill="#0DCaf0"
                        radius={[4, 4, 0, 0]}
                        barSize={30}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN - Weather & Transactions */}
        <div className="col-lg-4">
          <div
            className="card border-0 shadow-sm mb-4 text-white overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #2196F3, #6366f1)",
            }}
          >
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-start">
                {/* LEFT SIDE */}
                <div>
                  <h6 className="mb-0">{currentTime}</h6>
                  <p className="opacity-75 small">
                    {moment().format("ddd, DD MMM")}
                  </p>

                  {/* <div className="d-flex align-items-center opacity-75 small">
                    <MapPin size={12} />
                    <span className="fw-semibold ps-2">
                      {activeBusiness?.city || "Select your city"}
                    </span>
                  </div> */}
                </div>

                {/* RIGHT SIDE WEATHER */}

                <div className="text-end">
                  {/* If city & weather available → show weather */}
                  {hasCity && hasWeather ? (
                    <div>
                      <h6 className="display-6 fw-bold">
                        {Math.round(weather.main.temp)}°
                      </h6>
                      <p className="fw-bold text-capitalize small">
                        {weather.weather?.[0]?.description}
                      </p>
                    </div>
                  ) : (
                    /* Otherwise show image button */
                    <img
                      src="/assets/img/Frame_338.png"
                      alt="weather"
                      width={40}
                      height={40}
                      style={{ objectFit: "contain" }}
                    />
                  )}
                </div>
              </div>

              {/* SECOND ROW → WIND + HUMIDITY (ONLY AFTER API) */}
              {hasCity && hasWeather && (
                <div className="row mt-2 pt-2 border-top border-white border-opacity-25">
                  <div className="col-6">
                    <div className="d-flex align-items-center gap-2">
                      <Wind size={16} className="opacity-75" />
                      <span className="small">
                        {weather?.wind?.speed != null
                          ? (weather.wind.speed * 3.6).toFixed(1)
                          : "0.0"}{" "}
                        km/h
                      </span>
                    </div>
                  </div>

                  <div className="col-6 text-end">
                    <span className="small opacity-75">
                      Humidity: {weather?.main?.humidity}%
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
              <h6 className="mb-0 fw-bold">Recent Transactions</h6>
              <Link to="/transaction" className="small text-decoration-none">
                View All
              </Link>
            </div>
            <div className="card-body p-0">
              <div className="list-group list-group-flush">
                {transactions?.length > 0 ? (
                  transactions.slice(0, 8).map((t) => (
                    <div
                      key={t._id}
                      className="list-group-item border-0 px-3 py-3 border-bottom"
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <p
                            className="mb-0 small fw-bold text-dark text-truncate"
                            style={{ maxWidth: "140px" }}
                          >
                            {t.party?.name || t.partyName || t.expenseTitle}
                          </p>
                          <span
                            className="x-small text-muted"
                            style={{ fontSize: "11px" }}
                          >
                            {moment(t.date).format("DD MMM")} •{" "}
                            {t.referenceType}
                          </span>
                        </div>
                        <div className="text-end">
                          <p
                            className={`mb-0 small fw-bold ${
                              t.dueAmount > 0 ? "text-danger" : "text-success"
                            }`}
                          >
                            ₹{" "}
                            {(Number(t.totalAmount) || 0).toLocaleString(
                              "en-IN",
                            )}
                          </p>
                          <span
                            className="opacity-75"
                            style={{ fontSize: "11px" }}
                          >
                            {t.dueAmount > 0 ? "Due" : "Paid"}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  /* EMPTY STATE */
                  <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
                    <div
                      className="mb-2 d-flex align-items-center justify-content-center"
                      style={{
                        width: "50px",
                        height: "50px",
                        borderRadius: "50%",
                        background: "#f1f3f5",
                      }}
                    >
                      <i className="bi bi-arrow-left-right fs-4"></i>{" "}
                    </div>
                    <p className="mb-1 fw-semibold">No transactions yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default headerLayoutWrapper(Dashboard);
